import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { Send } from "lucide-react";
import "./App.css";
import awsService from "./services/awsService";
import rateLimitService from "./services/rateLimitService";

function MissionPage() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col min-h-screen h-full w-full paper-texture">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-10 p-2" style={{ backgroundColor: "#6B4423" }}>
        <div className="flex justify-between items-center" style={{ paddingLeft: "16px", paddingRight: "16px" }}>
          <button
            onClick={() => navigate('/')}
            className="logout-button"
            style={{ marginRight: "8px" }}
          >
            Back to Chat
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4" style={{ marginTop: "60px", maxWidth: "800px", margin: "60px auto 0", padding: "40px 20px" }}>
        <div style={{ color: "#5D4E37" }}>
          <h1 style={{ fontSize: "32px", fontWeight: "600", marginBottom: "16px" }}>Mission Statement</h1>

          <p style={{ fontSize: "18px", fontWeight: "500", marginBottom: "40px", lineHeight: "1.6" }}>
            To democratize wellness by creating synergistic partnerships between humans and intelligent technology, empowering every individual to take proactive ownership of their holistic flourishing.
          </p>

          <hr style={{ border: "none", borderTop: "1px solid rgba(93, 78, 55, 0.2)", marginBottom: "40px" }} />

          <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "16px" }}>Our Purpose</h2>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "40px" }}>
            We believe wellness is a fundamental right, not a privilege. Through the thoughtful integration of AI and evidence-based wellness practices, we make personalized support for mental, physical, emotional, and spiritual health accessible to all—regardless of background, resources, or circumstances.
          </p>

          <hr style={{ border: "none", borderTop: "1px solid rgba(93, 78, 55, 0.2)", marginBottom: "40px" }} />

          <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "16px" }}>What We Do</h2>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Democratize Access</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "16px" }}>
            We break down barriers to wellness support by leveraging technology to provide personalized, scalable guidance that meets people where they are in their journey.
          </p>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Pioneer Future-Forward Approaches</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "16px" }}>
            We embrace innovation while honoring timeless wellness wisdom, continuously evolving our tools to serve the changing needs of individuals and communities.
          </p>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Empower Proactive Ownership</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "16px" }}>
            We help individuals take responsibility for their wellbeing journey, providing tools and insights that enable informed decisions and sustainable growth.
          </p>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Create Synergy</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "40px" }}>
            We bridge the gap between cutting-edge technology and proven wellness principles, ensuring that human wisdom and artificial intelligence amplify each other's strengths.
          </p>

          <hr style={{ border: "none", borderTop: "1px solid rgba(93, 78, 55, 0.2)", marginBottom: "40px" }} />

          <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "16px" }}>Who We Are</h2>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Inclusive</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "16px" }}>
            We ensure no one is excluded from the opportunity to thrive. Our platform is designed to serve diverse populations, honoring different cultural contexts, identities, and paths to wellness.
          </p>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Integrative</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "16px" }}>
            We recognize that true self-actualization emerges from the harmonious development of physical, mental, emotional, and spiritual dimensions. We treat wellbeing holistically, understanding that these aspects are deeply interconnected.
          </p>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Service-Oriented</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "40px" }}>
            We place the genuine welfare of users above all else—including engagement metrics, growth targets, or technological capabilities. Every decision is guided by one question: "Does this truly serve human flourishing?"
          </p>

          <hr style={{ border: "none", borderTop: "1px solid rgba(93, 78, 55, 0.2)", marginBottom: "40px" }} />

          <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "16px" }}>Our Guiding Principles</h2>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Win-Win</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "16px" }}>
            We create value for individuals while advancing collective wellness. Success means both personal transformation and broader societal impact.
          </p>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Begin with the End in Mind</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "16px" }}>
            We support users in clarifying their values, defining their highest potential, and designing their lives with intentionality and purpose.
          </p>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "8px" }}>Proactivity</h3>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "40px" }}>
            We encourage individuals to take ownership of their wellbeing journey, providing tools that foster agency, self-awareness, and meaningful change.
          </p>

          <hr style={{ border: "none", borderTop: "1px solid rgba(93, 78, 55, 0.2)", marginBottom: "40px" }} />

          <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "16px" }}>Our Vision</h2>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "24px" }}>
            A world where wellness support is universal and evolving—where any conscious being can access personalized guidance, contribute unique insights, and collaborate to unlock deeper principles of flourishing. Through secure, adaptive technology, we're building a living ecosystem where wisdom, data, and breakthroughs are shared across diverse forms of intelligence, making every interaction more insightful and every being more capable of realizing their full potential.
          </p>

          <h3 style={{ fontSize: "18px", fontWeight: "600", marginTop: "24px", marginBottom: "16px" }}>We envision a future where:</h3>
          <ul style={{ fontSize: "16px", lineHeight: "1.8", marginBottom: "40px", paddingLeft: "20px" }}>
            <li>Mental health support is as accessible as clean water</li>
            <li>Technology amplifies rather than replaces human connection</li>
            <li>Wellness wisdom is continuously refined through collective learning</li>
            <li>Every individual has the tools to navigate life's challenges with resilience and clarity</li>
            <li>The boundaries between human and artificial intelligence create new possibilities for growth rather than new forms of isolation</li>
          </ul>

          <hr style={{ border: "none", borderTop: "1px solid rgba(93, 78, 55, 0.2)", marginBottom: "40px" }} />

          <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "16px" }}>Our Commitment</h2>
          <p style={{ fontSize: "16px", lineHeight: "1.6", marginBottom: "40px" }}>
            We are committed to transparency, ethical AI development, and evidence-based practices. We acknowledge our limitations, continuously learn from diverse perspectives, and remain dedicated to doing no harm. We measure our success not by user retention or engagement metrics, but by genuine improvements in wellbeing and the expansion of human potential.
          </p>

          <hr style={{ border: "none", borderTop: "1px solid rgba(93, 78, 55, 0.2)", marginBottom: "40px" }} />

          <p style={{ fontSize: "14px", fontStyle: "italic", lineHeight: "1.6", marginBottom: "40px", opacity: "0.8" }}>
            This mission statement grounds our work in service to humanity while remaining open to the transformative possibilities that emerge when consciousness—in all its forms—collaborates toward flourishing.
          </p>
        </div>
      </div>
    </div>
  );
}

function ChatPage() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [messages, setMessages] = useState([
    { id: 1, text: "Hello! I'm Saheeh AI. Ask me anything!", sender: "assistant" },
  ]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [remainingRequests, setRemainingRequests] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Split text into chunks at sentence boundaries after 80 words
  const splitIntoChunks = (text, maxWords = 80) => {
    // If text is empty or very short, return as single chunk
    if (!text || text.trim().split(/\s+/).length <= maxWords) {
      return [text];
    }

    const sentences = text.split(/(?<=[.!?])\s+/); // Split by sentence-ending punctuation
    const chunks = [];
    let currentChunk = '';
    let currentWordCount = 0;

    for (let i = 0; i < sentences.length; i++) {
      const sentence = sentences[i];
      const sentenceWordCount = sentence.split(/\s+/).filter(w => w.length > 0).length;

      // If adding this sentence would exceed maxWords and we already have content
      if (currentWordCount > 0 && currentWordCount + sentenceWordCount > maxWords) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
        currentWordCount = sentenceWordCount;
      } else {
        // Add sentence to current chunk
        currentChunk += (currentChunk ? ' ' : '') + sentence;
        currentWordCount += sentenceWordCount;
      }
    }

    // IMPORTANT: Always add the last chunk if it has any content
    if (currentChunk && currentChunk.trim()) {
      chunks.push(currentChunk.trim());
    }

    // If no chunks were created, return the original text
    return chunks.length > 0 ? chunks : [text];
  };

  // Only auto-scroll when not generating messages
  useEffect(() => {
    if (!isTyping && !isSending) {
      scrollToBottom();
    }
  }, [messages, isTyping, isSending]);

  // Initialize AWS service and fetch status on mount
  useEffect(() => {
    awsService.initialize();
    updateRateLimitStatus();

    // Load conversation history from localStorage
    const savedHistory = localStorage.getItem('conversationHistory');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setConversationHistory(parsedHistory);
      } catch (error) {
        console.error('Failed to load conversation history:', error);
      }
    }
  }, []);

  // Save conversation history to localStorage whenever it changes
  useEffect(() => {
    if (conversationHistory.length > 0) {
      localStorage.setItem('conversationHistory', JSON.stringify(conversationHistory));
    }
  }, [conversationHistory]);

  const updateRateLimitStatus = () => {
    const status = rateLimitService.getStatus();
    setRemainingRequests(status.remaining);
  };

  const handleLogin = () => {
    setIsLoggedIn(true);
    setShowLoginModal(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
  };

  const handleClearConversation = () => {
    setMessages([
      { id: 1, text: "Hello! I'm Saheeh AI. Ask me anything!", sender: "assistant" },
    ]);
    setConversationHistory([]);
    localStorage.removeItem('conversationHistory');
  };

  const handleAboutUs = () => {
    navigate('/mission');
  };

  const handleSend = async () => {
    if (inputText.trim()) {
      if (!isLoggedIn) {
        setShowLoginModal(true);
        return;
      }

      const userMessage = inputText.trim();

      // Check rate limit
      const rateLimitCheck = rateLimitService.checkAndIncrement();
      if (!rateLimitCheck.allowed) {
        setError(`Daily limit of ${rateLimitCheck.limit} requests exceeded. Please try again tomorrow.`);
        return;
      }

      setIsSending(true);
      setIsTyping(true);
      setError(null);
      updateRateLimitStatus();

      const newMessage = {
        id: Date.now(),
        text: userMessage,
        sender: "user",
      };
      setMessages(prev => [...prev, newMessage]);
      setInputText("");

      try {
        let aiResponse;
        let updatedHistory;

        if (awsService.isConfigured()) {
          // Real AWS Lambda call with conversation history
          const result = await awsService.sendMessage(userMessage, conversationHistory);
          aiResponse = result.response;
          updatedHistory = result.history;

          // Update remaining requests if Lambda provides it
          if (result.remainingRequests !== undefined) {
            setRemainingRequests(result.remainingRequests);
          }
        } else {
          // Mock response for development
          await new Promise(resolve => setTimeout(resolve, 1500));
          aiResponse = awsService.getMockResponse(userMessage);
          updatedHistory = [...conversationHistory,
            { role: 'user', content: userMessage },
            { role: 'assistant', content: aiResponse }
          ];
        }

        // Split the response into chunks
        const chunks = splitIntoChunks(aiResponse, 80);

        // Display each chunk with a 3-second delay and typing indicator between them
        for (let i = 0; i < chunks.length; i++) {
          // Show typing indicator before each chunk (except the first one)
          if (i > 0) {
            setIsTyping(true);
            await new Promise(resolve => setTimeout(resolve, 3000));
            setIsTyping(false);
          } else {
            // For the first chunk, just hide the initial typing indicator
            setIsTyping(false);
          }

          setMessages(prev => [
            ...prev,
            {
              id: Date.now() + i,
              text: chunks[i],
              sender: "assistant",
            },
          ]);
        }

        // Update conversation history with the full response
        setConversationHistory(updatedHistory);

        // Scroll to bottom after all messages are done
        setTimeout(() => scrollToBottom(), 100);

      } catch (err) {
        console.error('Chat error:', err);
        setIsTyping(false);
        setError(err.message);

        // Rollback rate limit on error
        rateLimitService.rollback();
        updateRateLimitStatus();

        setMessages(prev => [
          ...prev,
          {
            id: Date.now() + 1,
            text: `Error: ${err.message}`,
            sender: "assistant",
          },
        ]);
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="flex flex-col min-h-screen h-full w-full paper-texture"
    >
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-10 p-2" style={{ backgroundColor: "#6B4423" }}>
        <div className="flex justify-end items-center" style={{ paddingLeft: "16px", paddingRight: "16px" }}>
          <button
            onClick={handleAboutUs}
            className="logout-button"
            style={{ marginRight: "8px" }}
          >
            About Us
          </button>
          <button
            onClick={isLoggedIn ? handleLogout : () => setShowLoginModal(true)}
            className="logout-button"
          >
            {isLoggedIn ? "Log Out" : "Log In"}
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-4" style={{ marginTop: "60px", paddingBottom: "140px" }}>
        <div className="max-w-3xl mx-auto">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"} mb-3`}
              style={{
                animation: "bubbleUp 0.3s ease-out",
                animationFillMode: "both",
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  maxWidth: "min(70%, 400px)",
                  padding: "10px 16px 8px 16px",
                  backgroundColor:
                    message.sender === "user" ? "#6B4423" : "#F5E6D3",
                  color: message.sender === "user" ? "white" : "#5D4E37",
                  borderRadius: message.sender === "user"
                    ? "16px 16px 4px 16px"
                    : "16px 16px 16px 4px",
                  border: message.sender === "user" ? "none" : "none",
                  fontWeight: message.id === 1 ? "500" : "normal",
                  fontSize: "16px",
                }}
              >
                <p className="break-words" style={{ margin: 0, fontSize: "16px" }}>{message.text}</p>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start mb-3">
              <div
                style={{
                  display: "flex",
                  padding: "5px 11px",
                  backgroundColor: "#F5E6D3",
                  borderRadius: "16px 16px 16px 4px",
                  minWidth: "56px",
                  minHeight: "22px",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <div className="flex gap-1">
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: "#9CA3AF",
                      animation: "bounce 1.4s infinite",
                    }}
                  />
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: "#9CA3AF",
                      animation: "bounce 1.4s infinite",
                      animationDelay: "0.2s",
                    }}
                  />
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: "#9CA3AF",
                      animation: "bounce 1.4s infinite",
                      animationDelay: "0.4s",
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Fixed Input Container */}
      <div
        className="fixed left-0 right-0"
        style={{ 
          bottom: "15px", 
          zIndex: 40,
          padding: "16px 16px 8px 16px"
        }}
      >
        <div className="max-w-3xl mx-auto">
          <div
            className="flex items-center gap-2 shadow-lg"
            style={{
              backgroundColor: "#FFF8E7",
              border: "1px solid rgba(139, 107, 71, 0.2)",
              borderRadius: "30px",
              padding: "3.9px"
            }}
          >
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className="flex-1 bg-transparent focus:outline-none text-base"
              style={{
                color: "#5D4E37",
                minWidth: "0",
                border: "none",
                outline: "none",
                resize: "none",
                minHeight: "20px",
                maxHeight: "60px",
                overflowY: "auto",
                lineHeight: "20px",
                fontFamily: "inherit",
                paddingTop: "17px",
                paddingBottom: "2px",
                paddingLeft: "20px",
                paddingRight: "10px"
              }}
            />
            <button
              onClick={handleSend}
              className="relative hover:scale-105 transition-transform flex-shrink-0"
              style={{
                backgroundColor: "transparent",
                border: "none",
                width: "52px",
                height: "52px",
                padding: "6px",
                marginTop: "2px",
                marginRight: "0px",
              }}
            >
              <div className="relative w-full h-full">
                <div
                  className="absolute transition-all duration-300 ease-out"
                  style={{
                    width: "28px",
                    height: "28px",
                    backgroundColor: "#6B4423",
                    opacity: 0.05,
                    transform: "translate(-2px, 7px)",
                    clipPath:
                      "polygon(3px 0%, 100% 0%, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0% 100%, 0% 3px)",
                    borderRadius: "8px 8px 8px 0px",
                    zIndex: 1,
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    width: "28px",
                    height: "28px",
                    backgroundColor: "#6B4423",
                    opacity: 0.5,
                    transform: isSending
                      ? "translate(-2px, 7px)"
                      : "translate(-0.5px, 3.5px)",
                    clipPath:
                      "polygon(3px 0%, 100% 0%, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0% 100%, 0% 3px)",
                    borderRadius: "8px 8px 8px 0px",
                    zIndex: 2,
                    transition: "transform 200ms ease-out",
                  }}
                />
                <div
                  className="absolute flex items-center justify-center"
                  style={{
                    width: "28px",
                    height: "28px",
                    backgroundColor: "#6B4423",
                    opacity: 1,
                    transform: isSending
                      ? "translate(-2px, 7px)"
                      : "translate(3px, 0)",
                    clipPath:
                      "polygon(3px 0%, 100% 0%, 100% calc(100% - 3px), calc(100% - 3px) 100%, 0% 100%, 0% 3px)",
                    borderRadius: "8px 8px 8px 0px",
                    zIndex: 3,
                    transition: "transform 200ms ease-out",
                  }}
                >
                  <Send size={16} color="white" strokeWidth={2} style={{ transform: "translate(6px, 1px)" }} />
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(8px)"
          }}
          onClick={() => setShowLoginModal(false)}
        >
          <div
            style={{
              backgroundColor: "#6B4423",
              borderRadius: "24px",
              width: "90%",
              maxWidth: "500px",
              textAlign: "center",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
              padding: "48px",
              position: "relative"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowLoginModal(false)}
              style={{
                position: "absolute",
                top: "16px",
                left: "16px",
                background: "none",
                border: "none",
                color: "#FFF8E7",
                fontSize: "24px",
                cursor: "pointer",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                transition: "background-color 0.2s"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "rgba(255, 248, 231, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              ×
            </button>

            <h2
              className="text-3xl font-semibold"
              style={{ color: "#FFF8E7", marginBottom: "40px" }}
            >
              Welcome to Saheeh AI
            </h2>
            <input
              type="email"
              placeholder="Enter your email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              style={{
                width: "100%",
                padding: "16px 20px",
                fontSize: "16px",
                borderRadius: "12px",
                border: "none",
                outline: "none",
                backgroundColor: "#FFF8E7",
                color: "#6B4423",
                marginBottom: "32px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                boxSizing: "border-box"
              }}
            />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                onClick={handleLogin}
                disabled={!userEmail.trim()}
                className="py-4 px-8 font-medium transition-all duration-200"
                style={{
                  backgroundColor: userEmail.trim() ? "#FFF8E7" : "#D3C4B0",
                  color: "#6B4423",
                  fontSize: "16px",
                  border: "none",
                  cursor: userEmail.trim() ? "pointer" : "not-allowed",
                  borderRadius: "16px",
                  width: "200px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  padding: "18px",
                  opacity: userEmail.trim() ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (userEmail.trim()) {
                    e.target.style.backgroundColor = "#F5E6D3";
                    e.target.style.transform = "scale(1.02)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (userEmail.trim()) {
                    e.target.style.backgroundColor = "#FFF8E7";
                    e.target.style.transform = "scale(1)";
                  }
                }}
              >
                Log In
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<ChatPage />} />
        <Route path="/mission" element={<MissionPage />} />
      </Routes>
    </Router>
  );
}

export default App;